import Image from 'next/image';
import {defaultThemeImagePaths} from '@helpers/config-master.mjs';
import cn from 'clsx';
import HorizontalSlider from '@components/widgets/HorizontalSlider/HorizontalSlider';
import {Fragment, useCallback, useState} from 'react';
import {
  DesktopPreOrderOrSaleProductImageOverlay
} from '@components/product/global/ProductImageOverlays';
import {
  ModalGalleryV3_YARLB
} from '@components/product/desktop/ModalGalleryYetAnotherReactLightBox';

export const SingleProductFocusGallery = ({variants, product}) => {
  // console.log("[SingleProductFocusGallery] product:", product)
  // console.log("[SingleProductFocusGallery] variants:", variants)
  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);

  const sku = product?.sku;

  let currentVariant = variants?.find((x) => x?.sku === sku);

  let allImages = currentVariant?.images ?? [];
  let firstImageSrc = typeof allImages?.[0]?.url === 'string' ? allImages?.[0]?.url.split('?')[0] : null;

  const isDailyDeal = product?.category === 'custom-bundle';
  if (isDailyDeal) {
    allImages = product?.images ?? [];
    firstImageSrc = product?.mainImage
  }

  const isInStock = product?.isInStock || product?.currentlyInStock;
  const preOrderInfo = product?.pre_order_info || product?.preOrderInfo;
  const prodName = product?.name || product?.productTitle || product?.siteTitle || product?.siteName;

  const desktopProductImageSaleOverlay = isDailyDeal
    ? defaultThemeImagePaths?.desktopProductImageSaleOverlayDailyDeal
    : defaultThemeImagePaths?.desktopProductImageSaleOverlay;
  const mobileProductImageSaleOverlay = isDailyDeal
    ? defaultThemeImagePaths?.mobileProductImageSaleOverlayDailyDeal
    : defaultThemeImagePaths?.mobileProductImageSaleOverlay;

  const openLightbox = useCallback((index = 0) => {
    setCurrentImage(index);
    setViewerIsOpen(true);
  }, []);

  const closeLightbox = () => {
    setCurrentImage(0);
    setViewerIsOpen(false);
  };

  if (!allImages?.length) return null;

  return (
    <div
      className="flex flex-col gap-4 md:h-auto place-content-start outline-none relative"
      tabIndex="-1">
      {firstImageSrc && (
        <div className="relative hidden lg:block">
          <Image
            src={firstImageSrc}
            onClick={() => openLightbox(0)}
            alt={`Featured Product Photo for ${prodName}`}
            width={750}
            height={750}
            loading="eager"
            className="w-[100vw] md:w-full h-full md:h-auto flex-shrink-0 md:flex-shrink-none md:col-span-2 rounded cursor-pointer"
          />

          <DesktopPreOrderOrSaleProductImageOverlay
            isInStock={isInStock}
            preOrderInfo={preOrderInfo}
            desktopProductImageSaleOverlay={desktopProductImageSaleOverlay}
          />
        </div>
      )}

      <div className="relative">
        <HorizontalSlider showDots={true} showDotsMobileOnly={true} showScrollbar={false}>
          {allImages?.map((img, index) => {
            // console.log('[SingleProductFocusGallery] img:', img);
            const image = typeof img?.url === 'string' ? img?.url?.split('?')[0] : null;
            const isFirstImage = index === 0;
            const isLastImage = index === allImages.length - 1;
            return (
              <Fragment key={`${sku}-image${index}`}>
                {img?.url && (
                  <div
                    className={cn(
                      allImages?.length > 1 ? 'w-[85vw]' : 'w-full',
                      'aspect-[1.5] snap-center snap-mandatory relative',
                      'lg:w-[172px] lg:h-[172px] lg:aspect-auto transition-all border border-neutral-400 flex-shrink-0 rounded cursor-pointer overflow-hidden',
                      {
                        'lg:block ml-[2.5vw] lg:ml-0': isFirstImage && allImages?.length > 1,
                        'lg:block mr-[2.5vw] lg:mr-0': isLastImage && allImages?.length > 1
                      }
                    )}>
                    <Image
                      src={image}
                      onClick={(e) => {
                        e.preventDefault();
                        openLightbox(index);
                      }}
                      width={400}
                      height={400}
                      alt={`Featured Product Photo ${index + 1} for ${prodName}`}
                      className="transition-all duration-300 lg:hover:scale-105 object-contain h-full"
                      sizes="(min-width: 992px) 175px, 85vw"
                    />

                    {index === 0 && (
                      <div className="lg:hidden">
                        <div
                          className="absolute z-10 top-0 right-0 w-3/4 h-3/4 pointer-events-none">
                          {mobileProductImageSaleOverlay && (
                            <Image
                              src={mobileProductImageSaleOverlay}
                              alt="Sale Info Overlay"
                              priority
                              style={{objectFit: 'fill'}}
                              width={600}
                              height={600}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Fragment>
            );
          })}
        </HorizontalSlider>
      </div>

      <ModalGalleryV3_YARLB
        sku={sku}
        images={allImages}
        viewerIsOpen={viewerIsOpen}
        closeLightbox={closeLightbox}
        photoIndex={currentImage}
        setPhotoIndex={setCurrentImage}
      />
    </div>
  );
};
